import axios from "axios";
import { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend
} from "chart.js";
import { MdError } from "react-icons/md";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const InventoryHistoryChart = ({ outlet }) => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const token = sessionStorage.getItem('token');

    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchCostingAnalysis = async () => {
        if (!token || !outlet) return;

        setLoading(true);
        setError(null);

        try {
            const response = await axios.post(`${baseUrl}/food-and-costing-analysis-multiple`, {
                token,
                outlet
            });

            const data = response.data?.snapshots || [];
            setChartData(data);
        } catch (error) {
            const errorMsg = error.response?.data?.error || "Failed to fetch food and costing analysis data";
            setError(errorMsg);
            console.error("Costing Analysis Chart Error", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCostingAnalysis();
    }, [token, outlet]);

    const extractChartData = (type) => {
        const labels = chartData.map(snap => `${snap.start_date} to ${snap.end_date}`);
        const actual = [];
        const net = [];
        const tooltipMeta = [];

        chartData.forEach(snap => {
            const item = snap.data.find(d => d.item_type === type);
            if (item) {
                actual.push(parseFloat(item.cost_percent.actual_cost_percent));
                net.push(parseFloat(item.cost_percent.net_cost_percent));
                tooltipMeta.push({
                    actualConsumptionCost: item.cost_values.actual_consumption_cost,
                    grossCost: item.cost_values.gross_cost
                });
            } else {
                actual.push(0);
                net.push(0);
                tooltipMeta.push({ actualConsumptionCost: "-", grossCost: "-" });
            }
        });

        return {
            labels,
            datasets: [
                {
                    label: "Actual Cost Percent",
                    backgroundColor: "rgba(75,192,192)",
                    data: actual,
                    tooltipMeta
                },
                {
                    label: "Net Cost Percent",
                    backgroundColor: "rgba(153,102,255)",
                    data: net,
                    tooltipMeta
                }
            ]
        };
    };

    const chartOptions = (type) => ({
        responsive: true,
        plugins: {
            legend: { position: "top" },
            title: {
                display: true,
                text: `${type} - Actual vs Net Cost Percent`
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const value = context.raw;
                        const meta = context.dataset.tooltipMeta?.[context.dataIndex] || {};
                        const label = context.dataset.label;

                        let tooltipLine = `${label}: ${value.toFixed(2)}%`;
                        if (label === "Actual Cost Percent") {
                            tooltipLine += `, Actual Cost: ${meta.actualConsumptionCost || "-"}`;
                        } else if (label === "Net Cost Percent") {
                            tooltipLine += `, Gross Cost: ${meta.grossCost || "-"}`;
                        }

                        return tooltipLine;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                title: { display: true, text: "Cost %" }
            }
        }
    });

    return (
        <div className="my-6 mt-16 px-4">
            {loading ? (
                <div className="flex justify-center items-center h-[500px]">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
                </div>
            ) : error ? (
                <div className="rounded-md bg-red-50 p-4 max-w-lg mx-auto">
                    <div className="flex gap-3">
                        <div className="flex-shrink-0">
                            <MdError className="h-5 w-5 text-red-400" />
                        </div>
                        <div className="text-red-700">
                            {error}
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    <div className="h-[500px] flex justify-center">
                        <Bar
                            data={extractChartData("Food")}
                            options={chartOptions("Food")}
                        />
                    </div>

                    <div className="h-[500px] mt-16 flex justify-center">
                        <Bar
                            data={extractChartData("Beverage")}
                            options={chartOptions("Beverage")}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default InventoryHistoryChart;
